import React from "react"
import { Box, Stack, type BoxProps } from "@mui/material"
import { composeSx } from "../theme/composeSx"
import { resolveViraPalette } from "../theme/resolveViraPalette"
import { Heading, ViraText } from "../atoms/ViraText"
import { ViraIcon, type ViraIconName } from "../atoms/ViraIcon"

export type EmptyStateProps = BoxProps & {
  title: string
  description?: string
  icon?: ViraIconName
  actions?: React.ReactNode
  footer?: React.ReactNode
}

/** Centered empty placeholder with icon, title, and optional actions. */
export function EmptyState({
  title,
  description,
  icon = "mic",
  actions,
  footer,
  sx,
  ...rest
}: EmptyStateProps) {
  return (
    <Box sx={composeSx(() => ({ textAlign: "center" }), sx)} {...rest}>
      <Box
        sx={(theme) => {
          const vira = resolveViraPalette(theme)
          return {
            width: 56,
            height: 56,
            borderRadius: "50%",
            backgroundColor: vira.softSurface,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
          }
        }}
      >
        <ViraIcon name={icon} tone="muted" />
      </Box>
      <Heading level="card" sx={{ mb: 1 }}>
        {title}
      </Heading>
      {description ? (
        <ViraText
          tone="muted"
          sx={{
            fontSize: 14.5,
            maxWidth: 520,
            mx: "auto",
            mb: 3,
            lineHeight: 1.6,
          }}
        >
          {description}
        </ViraText>
      ) : null}
      {actions}
      {footer ? <Box sx={{ mt: 3 }}>{footer}</Box> : null}
    </Box>
  )
}

export type EmptyStateFooterLinksProps = {
  hint?: string
  children: React.ReactNode
}

export function EmptyStateFooterLinks({
  hint,
  children,
}: EmptyStateFooterLinksProps) {
  return (
    <>
      {hint ? (
        <ViraText tone="subtle" sx={{ mb: 1, fontSize: 11 }}>
          {hint}
        </ViraText>
      ) : null}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        justifyContent="center"
        alignItems="center"
        flexWrap="wrap"
        useFlexGap
        divider={
          <Box
            sx={(theme) => {
              const vira = resolveViraPalette(theme)
              return {
                color: vira.subtle,
                display: { xs: "none", sm: "block" },
              }
            }}
          >
            ·
          </Box>
        }
      >
        {children}
      </Stack>
    </>
  )
}
